/**
 * Utility functions to update meta tags for social media sharing (Open Graph, Twitter Cards)
 */

/**
 * Update or create a meta tag
 */
const updateMetaTag = (property, content) => {
  if (!content) return;

  // Remove existing tag if it exists
  const existingTag = document.querySelector(`meta[property="${property}"]`) || 
                     document.querySelector(`meta[name="${property}"]`);
  
  if (existingTag) {
    existingTag.setAttribute('content', content);
  } else {
    const meta = document.createElement('meta');
    if (property.startsWith('og:') || property.startsWith('twitter:')) {
      meta.setAttribute('property', property);
    } else {
      meta.setAttribute('name', property);
    }
    meta.setAttribute('content', content);
    document.head.appendChild(meta);
  }
};

/**
 * Update the page title
 */
const updateTitle = (title) => {
  document.title = title || 'Alenalki | Community News';
};

/**
 * Update all Open Graph and Twitter Card meta tags for sharing
 * @param {Object} data - Object containing title, description, image, url, type
 */
export const updateMetaTags = (data) => {
  const {
    title,
    description,
    image,
    url,
    type = 'article',
    siteName = 'Alenalki',
    author
  } = data;

  // Update page title
  if (title) {
    updateTitle(`${title} | Alenalki`);
  }

  // Basic meta tags
  updateMetaTag('description', description);

  // Open Graph tags
  updateMetaTag('og:title', title);
  updateMetaTag('og:description', description);
  updateMetaTag('og:image', image);
  updateMetaTag('og:url', url);
  updateMetaTag('og:type', type);
  updateMetaTag('og:site_name', siteName);
  if (author) {
    updateMetaTag('article:author', author);
  }

  // Twitter Card tags
  updateMetaTag('twitter:card', 'summary_large_image');
  updateMetaTag('twitter:title', title);
  updateMetaTag('twitter:description', description);
  updateMetaTag('twitter:image', image);

  // Additional meta tags
  if (type === 'article') {
    updateMetaTag('article:published_time', new Date().toISOString());
  }
};

/**
 * Clear/reset meta tags to default
 */
export const resetMetaTags = () => {
  // Remove all og: and twitter: meta tags
  const ogTags = document.querySelectorAll('meta[property^="og:"]');
  const twitterTags = document.querySelectorAll('meta[property^="twitter:"]');
  
  [...ogTags, ...twitterTags].forEach(tag => tag.remove());
  
  // Reset title
  document.title = 'Alenalki | Community News';
  
  // Reset description
  const descTag = document.querySelector('meta[name="description"]');
  if (descTag) {
    descTag.setAttribute('content', 'Stay informed with the latest news, blogs, and community updates from Alenalki');
  }
};

/**
 * Strip HTML tags and get plain text (for description)
 */
export const stripHtmlTags = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s{2,}/g, ' ').trim();
};

/**
 * Extract first image from HTML content
 */
export const extractFirstImageFromContent = (content) => {
  if (!content) return null;
  
  const imgRegex = /<img[^>]+src="([^">]+)"/i;
  const match = content.match(imgRegex);
  
  return match ? match[1] : null;
};

/**
 * Get full URL for sharing
 */
export const getFullUrl = (path) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}${path}`;
};

